import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { select, Store } from '@ngrx/store';
import { take } from 'rxjs/operators';
import moment from 'moment';
import { EquipmentService } from '@app/services/equipment.service';
import { environment } from 'environments/environment';
import { AppState } from '@app/core/reducers';
import { currentUser } from '@app/core/auth';
import { User } from '@app/model/user';

declare var Chart: any;

@Component({
  selector: 'app-my-equipment-statistics',
  templateUrl: './my-equipment-statistics.component.html',
  styleUrls: ['./my-equipment-statistics.component.scss'],
})
export class MyEquipmentStatisticsComponent implements OnInit, AfterViewInit {
  @ViewChild('statusChart', { static: false }) statusChartRef!: ElementRef;
  @ViewChild('brandChart', { static: false }) brandChartRef!: ElementRef;

  host: string = '';
  loading = false;
  currentUserId: string = '';

  // Filters
  dateSlected: any = {};
  statusFilter: string = '';
  topLimit: number = 10;
  topLimitOptions: number[] = [5, 10, 20, 50];

  // Data
  summary: any = null;
  byBrand: any[] = [];
  byModel: any[] = [];
  byUser: any[] = [];

  loadingRepairCost = false;
  repairCostSummary: any = null;
  repairCostRanked: any[] = [];
  expandedRepairEquipmentId: string | null = null;
  loadingRepairCostDetail = false;
  repairCostDetail: any[] = [];

  // Charts
  private statusChart: any = null;
  private brandChart: any = null;

  private readonly statusColors: Record<string, string> = {
    active: '#36a3eb',
    maintenance: '#ffb822',
    inactive: '#7b7f9e',
    retired: '#f4516c',
  };
  private readonly palette: string[] = [
    '#36a3eb',
    '#ffb822',
    '#0abb87',
    '#f4516c',
    '#8950FC',
    '#1bc5bd',
    '#fd7e14',
    '#20c997',
    '#6f42c1',
    '#e83e8c',
    '#17a2b8',
    '#ffc107',
  ];

  constructor(
    private equipmentService: EquipmentService,
    private toastr: ToastrService,
    private ref: ChangeDetectorRef,
    private router: Router,
    private store: Store<AppState>
  ) {}

  ngOnInit() {
    this.host = environment.backendhost;
    // Lấy userId hiện tại từ Store rồi mới load statistics
    this.store
      .pipe(select(currentUser), take(1))
      .subscribe((user: User) => {
        this.currentUserId = (user && user.id) || '';
        this.loadStatistics();
        this.loadRepairCostReport();
      });
  }


  private getDateRangeParams(): { fromDate?: string; toDate?: string } {
    const fromDate =
      this.dateSlected && this.dateSlected.startDate
        ? moment(this.dateSlected.startDate).format('YYYY-MM-DD')
        : undefined;
    const toDate =
      this.dateSlected && this.dateSlected.endDate
        ? moment(this.dateSlected.endDate).format('YYYY-MM-DD')
        : undefined;
    return { fromDate, toDate };
  }

  ngAfterViewInit() {}

  @HostListener('window:resize')
  onResize() {
    if (this.statusChart || this.brandChart) {
      setTimeout(() => this.renderCharts(), 100);
    }
  }

  loadStatistics() {
    if (!this.currentUserId) {
      return;
    }
    this.loading = true;
    const fromDate =
      this.dateSlected && this.dateSlected.startDate
        ? moment(this.dateSlected.startDate).format('YYYY-MM-DD')
        : undefined;
    const toDate =
      this.dateSlected && this.dateSlected.endDate
        ? moment(this.dateSlected.endDate).format('YYYY-MM-DD')
        : undefined;
    this.equipmentService
      .getEquipmentStatistics({
        userId: this.currentUserId,
        status: this.statusFilter || undefined,
        fromDate: fromDate,
        toDate: toDate,
        topLimit: this.topLimit,
      })
      .subscribe((res) => {
        this.loading = false;
        if (res && res.success && res.data) {
          this.summary = res.data.summary || null;
          this.byBrand = res.data.by_brand || [];
          this.byModel = res.data.by_model || [];
          this.byUser = res.data.by_user || [];
          this.ref.detectChanges();
          setTimeout(() => this.renderCharts(), 0);
        } else if (res && res.data === null) {
          // API returned error
          this.toastr.error(res.message || 'Lỗi khi tải thống kê thiết bị');
        } else if (res && res.data) {
          // Some backends return data without `success` flag
          this.summary = res.data.summary || null;
          this.byBrand = res.data.by_brand || [];
          this.byModel = res.data.by_model || [];
          this.byUser = res.data.by_user || [];
          this.ref.detectChanges();
          setTimeout(() => this.renderCharts(), 0);
        } else {
          this.toastr.error((res && res.message) || 'Lỗi khi tải thống kê thiết bị');
        }
      });
  }

  private renderCharts() {
    this.renderStatusChart();
    this.renderBrandChart();
  }

  private renderStatusChart() {
    if (!this.statusChartRef) {
      return;
    }
    const data = (this.summary && this.summary.by_status) || [];
    const labels = data.map((x: any) => this.getStatusLabel(x.status));
    const values = data.map((x: any) => x.count);
    const colors = data.map((x: any) => this.statusColors[x.status] || '#999');

    if (this.statusChart) {
      this.statusChart.destroy();
    }
    this.statusChart = new Chart(this.statusChartRef.nativeElement, {
      type: 'doughnut',
      data: {
        labels,
        datasets: [{ data: values, backgroundColor: colors }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutoutPercentage: 60,
        legend: { position: 'bottom' },
        tooltips: {
          callbacks: {
            label: (tooltipItem: any, chartData: any) => {
              const value = chartData.datasets[0].data[tooltipItem.index];
              const total = chartData.datasets[0].data.reduce((a: number, b: number) => a + b, 0);
              const percent = total ? ((value / total) * 100).toFixed(1) : '0';
              return `${chartData.labels[tooltipItem.index]}: ${value} (${percent}%)`;
            },
          },
        },
      },
    });
  }

  private aggregateTopOthers(data: any[], labelKey: string, topN: number = 4): any[] {
    if (!data || data.length <= topN) {
      return data ? data.slice() : [];
    }
    const top = data.slice(0, topN);
    const others = data.slice(topN);
    const othersCount = others.reduce((a: number, b: any) => a + (b.count || 0), 0);
    const othersPercentage = others.reduce((a: number, b: any) => a + (b.percentage || 0), 0);
    const othersItem: any = {
      [labelKey]: 'Khác',
      count: othersCount,
      percentage: othersPercentage,
      _isOthers: true,
    };
    if (labelKey === 'model_name') {
      othersItem.brand_name = '';
    }
    top.push(othersItem);
    return top;
  }

  private renderBrandChart() {
    if (!this.brandChartRef) {
      return;
    }
    const data = this.aggregateTopOthers(this.byBrand, 'brand_name', 4);
    const labels = data.map((x: any) => x.brand_name || 'Khác');
    const values = data.map((x: any) => x.count);
    const colors = data.map((_: any, i: number) =>
      i < 4 ? this.palette[i] : '#7b7f9e'
    );

    if (this.brandChart) {
      this.brandChart.destroy();
    }
    this.brandChart = new Chart(this.brandChartRef.nativeElement, {
      type: 'bar',
      data: {
        labels,
        datasets: [
          {
            label: 'Số lượng',
            data: values,
            backgroundColor: colors,
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        scales: {
          yAxes: [{ ticks: { beginAtZero: true, precision: 0 } }],
        },
        tooltips: {
          callbacks: {
            label: (tooltipItem: any, chartData: any) => {
              const value = chartData.datasets[0].data[tooltipItem.index];
              const percent = data[tooltipItem.index] ? data[tooltipItem.index].percentage : 0;
              return `${chartData.labels[tooltipItem.index]}: ${value} (${percent}%)`;
            },
          },
        },
      },
    });
  }

  applyFilter() {
    this.loadStatistics();
    this.loadRepairCostReport();
  }

  onDateRangeChange() {
    this.loadStatistics();
    this.loadRepairCostReport();
  }

  resetFilter() {
    this.dateSlected = { startDate: null, endDate: null };
    this.statusFilter = '';
    this.topLimit = 10;
    this.loadStatistics();
    this.loadRepairCostReport();
  }

  // ---------------------- Repair cost report ----------------------

  loadRepairCostReport(): void {
    // No need for currentUserId: the API resolves the current user from the token and takes no userId param.
    this.loadingRepairCost = true;
    this.expandedRepairEquipmentId = null;
    this.repairCostDetail = [];
    const { fromDate, toDate } = this.getDateRangeParams();
    this.equipmentService
      .getEquipmentRepairCostReport({
        fromDate,
        toDate,
        topLimit: this.topLimit
      })
      .subscribe((res) => {
        this.loadingRepairCost = false;
        if (res && res.success && res.data) {
          this.repairCostSummary = res.data.summary || null;
          this.repairCostRanked = res.data.ranked_equipments || [];
        } else {
          this.repairCostSummary = null;
          this.repairCostRanked = [];
        }
        this.ref.detectChanges();
      });
  }

  toggleRepairCostDetail(equipment: any): void {
    const id = equipment && equipment.my_equipment_id;
    if (!id) {
      return;
    }
    if (this.expandedRepairEquipmentId === id) {
      this.expandedRepairEquipmentId = null;
      this.repairCostDetail = [];
      return;
    }
    this.expandedRepairEquipmentId = id;
    this.repairCostDetail = [];
    this.loadingRepairCostDetail = true;
    const { fromDate, toDate } = this.getDateRangeParams();
    this.equipmentService.getEquipmentRepairCostDetail(id, fromDate, toDate).subscribe((res) => {
      this.loadingRepairCostDetail = false;
      this.repairCostDetail = (res && res.success && res.data) || [];
      this.ref.detectChanges();
    });
  }

  formatCurrency(value: number): string {
    return (value || 0).toLocaleString('vi-VN');
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'active':
        return 'Đang hoạt động';
      case 'inactive':
        return 'Ngừng hoạt động';
      case 'maintenance':
        return 'Bảo trì';
      case 'retired':
        return 'Ngừng sử dụng';
      default:
        return status || '';
    }
  }

  getStatusColor(status: string): string {
    return this.statusColors[status] || '#999';
  }

  getBrandLogo(logo: string): string {
    if (!logo) {
      return '';
    }
    return logo.indexOf('http') === 0 ? logo : this.host + logo;
  }

  getImageUrl(path?: string): string {
    if (!path) {
      return '';
    }
    return path.indexOf('http') === 0 ? path : this.host + path;
  }

  goBackToEquipment() {
    this.router.navigate(['/myprofile'], { fragment: 'my-equipment' });
  }
}
